import React from 'react'
import { Modal, Button, Tag } from 'antd'
import { Integration } from '../types'
import { theme } from '../../../theme'
import { styled } from '../../../theme'

interface LearnMoreModalProps {
  visible: boolean
  integration: Integration | null
  onCancel: () => void
  onRequest: (integration: Integration) => void
}

const ModalContent = styled.div`
  .logo-section {
    display: flex;
    gap: 24px;
    margin-bottom: 0px;
  }

  .logo-container {
    flex-shrink: 0;
    width: 140px;
    height: 140px;
    background: white;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-container img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-width: 100%;
    max-height: 100%;
  }

  .logo-container img[src$='.svg'],
  .logo-container img[src$='.SVG'] {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-width: 100%;
    max-height: 100%;
  }

  .info-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 0px;
  }

  .integration-name {
    font-size: 24px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.softBlack};
    margin: 0;
    margin-bottom: 0;
  }

  .short-description {
    font-size: 16px;
    color: ${(props) => props.theme.colors.softBlack};
    line-height: 1.5;
    margin-bottom: 0;
  }

  .tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .section-heading {
    font-size: 18px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.softBlack};
    margin: 20px 0 16px 0;
  }

  .overview-description {
    font-size: 14px;
    color: ${(props) => props.theme.colors.softBlack};
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .videos-container,
  .images-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 0px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .video-item,
  .image-item {
    transition: transform 0.2s;
    min-height: 230px;
  }

  .video-item:hover,
  .image-item:hover {
    transform: translateY(-4px);
  }

  .image-item {
    cursor: pointer;
  }

  .image-thumbnail {
    width: 100%;
    height: 230px;
    background: ${(props) => props.theme.colors.grayBackground || '#f5f5f5'};
    border-radius: 8px;
    margin-bottom: 8px;
    position: relative;
    overflow: hidden;
  }

  .image-thumbnail.clickable img:hover {
    box-shadow: 0px 4px 12px rgba(48, 215, 186, 0.3);
  }

  .image-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-player-container {
    width: 100%;
    margin: 0;
    margin-bottom: 8px;
    border-radius: 8px;
    overflow: hidden;
    height: 230px;
  }

  .video-player-inline {
    width: 100%;
    height: 230px;
    border-radius: 8px;
    overflow: hidden;
    object-fit: cover;
  }

  .video-title,
  .image-title {
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.softBlack};
    margin-bottom: 4px;
    cursor: default;
    user-select: none;
  }

  .video-description,
  .image-description {
    font-size: 12px;
    color: ${(props) => props.theme.colors.grey6 || '#666'};
    cursor: default;
    user-select: none;
  }
`

export const LearnMoreModal: React.FC<LearnMoreModalProps> = ({
  visible,
  integration,
  onCancel,
  onRequest,
}) => {

  if (!integration) return null

  const categories = integration.category.split(',').map((cat) => cat.trim())
  const propertyTypes = integration.property_type.split(',').map((pt) => pt.trim())
  
  const videos = integration.videos?.filter(v => v?.url) || []
  const images = integration.images?.filter(img => img?.image_url?.trim()) || []
  
  const videosToShow = videos.length >= 2 ? videos.slice(0, 2) : videos.slice(0, 1)
  const imagesToShow = videos.length === 1 ? images.slice(0, 1) : images.slice(0, 2)
  const hasContent = videosToShow.length > 0 || imagesToShow.length > 0

  const convertToEmbedUrl = (url: string): string => {
    let videoId: string | null = null
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0] || null
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || null
    } else if (url.includes('youtube.com/embed/') || url.includes('youtube-nocookie.com/embed/')) {
      videoId = url.match(/embed\/([^?]+)/)?.[1] || null
    }
    
    if (videoId) {
      const params = new URLSearchParams()
      params.set('rel', '0')
      params.set('modestbranding', '1')
      return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`
    }
    
    return url
  }

  const handleRequestClick = () => {
    onCancel()
    onRequest(integration)
  }

  const handleImageClick = (imageLink: string | null | undefined) => {
    if (imageLink) {
      window.open(imageLink, '_blank', 'noopener,noreferrer')
    }
  }

  const renderImage = (image: typeof images[0], index: number) => {
    const hasLink = !!image.image_link
    return (
      <div key={`image-${index}`} className="image-item">
        <div 
          className={`image-thumbnail ${hasLink ? 'clickable' : ''}`}
          style={{ cursor: hasLink ? 'pointer' : 'default' }}
          onClick={() => handleImageClick(image.image_link)}
        >
          <img 
            src={image.image_url || ''} 
            alt={image.image_title || `${integration.name} image ${index + 1}`}
          />
        </div>
        {image.image_title && (
          <div 
            className="video-title"
            style={{ 
              cursor: hasLink ? 'pointer' : 'default',
              textDecoration: hasLink ? 'underline' : 'none'
            }}
            onClick={() => handleImageClick(image.image_link)}
          >
            {image.image_title}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button key="request" type="primary" onClick={handleRequestClick}>
            INTERESTED? CONTACT US
          </Button>
        </div>
      }
      width={900}
      closable={true}
    >
      <ModalContent>
        <div className="logo-section">
          <div className="logo-container">
            <img src={integration.logo_url || ''} alt={integration.name} />
          </div>
          <div className="info-section">
            <h2 className="integration-name" style={{ marginBottom: '0' }}>{integration.name}</h2>
            <p className="short-description" style={{ marginBottom: '0' }}>{integration.short_description}</p>
            <div className="tags-container">
              {categories.map((cat, index) => (
                <Tag key={`cat-${index}`} style={{ margin: '0px' }}>
                  {cat}
                </Tag>
              ))}
              {propertyTypes.map((pt, index) => (
                <Tag key={`pt-${index}`} style={{ backgroundColor: 'white', border: '1px solid lightgray', margin: '0px' }}>
                  {pt}
                </Tag>
              ))}
            </div>
          </div>
        </div>

        <h3 className="section-heading" style={{ marginBottom: '5px' }}>Overview</h3>
        <p className="overview-description">{integration.description}</p>

        {hasContent && (
          <>
            {videosToShow.length > 0 && (
              <h3 className="section-heading" style={{ marginTop: '0px' }}>See it in Action</h3>
            )}
            <div className="videos-container">
              {videosToShow.map((video, index) => (
                <div key={index} className="video-item">
                  <div className="video-player-container">
                    <iframe
                      src={convertToEmbedUrl(video.url)}
                      className="video-player-inline"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="video-title">{video.title}</div>
                  <div className="video-description">{video.description}</div>
                </div>
              ))}
              {imagesToShow.map((image, index) => renderImage(image, index))}
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

